import { Flex } from '@zillow/constellation';
import React, { useCallback } from 'react';
import { IEscrowWireDisbursementsData } from 'types/order/escrow/IEscrowWireDisbursementsData';
import { voidWireSchema } from './schemas';
import { VoidConfirmationModal } from './VoidConfirmationModal';
import { VoidReason } from './VoidReason';
import { Alert } from '@zillow/constellation';
import { useSelectCurrentOrderId } from 'selectors/order/selectCurrentOrder';
import { assertDefined } from 'utilities/Assertions';
import { useVoidWireDisbursementMutation } from 'API/order/escrow/WireDisbursementAPI';
import { IVoidData } from './IVoidData';
import { checkForMutationSuccess } from 'API/checkForMutationSuccess';
import { VoidWireDetails } from './VoidWireDetails';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';
import { Confirmation } from './Confirmation';
import { IWireDisbursementFormData } from 'types/order/escrow/wires/IWireDisbursementFormData';
import { eVoidReason, voidReasonOptions } from 'types/accounting/eVoidReason';
import { useHasPermissions } from 'components/accessControl/hooks/useHasPermissions';

interface IVoidWireButtonProps {
  wireDisbursement: IWireDisbursementFormData | IEscrowWireDisbursementsData;
}

export const VoidWireButton: React.FC<IVoidWireButtonProps> = ({ wireDisbursement }) => {
  const orderId = useSelectCurrentOrderId();
  assertDefined(orderId);

  const [onVoidWire] = useVoidWireDisbursementMutation();

  const onVoid = useCallback(
    async (values: IVoidData) => {
      const result = await onVoidWire({
        orderId,
        wire: {
          id: wireDisbursement.id,
          void_reason: values.voidReason as eVoidReason,
          void_reason_description:
            values.voidReason === eVoidReason.Other || values.voidReason === eVoidReason.SentInError
              ? values.voidReasonDescription
              : undefined,
        },
      });
      checkForMutationSuccess(result, 'voiding wire');
    },
    [orderId, wireDisbursement.id, onVoidWire]
  );

  const isVoidable = !wireDisbursement.voidedAt && wireDisbursement.enqueuedForDisbursementAt;
  const hasPermission = useHasPermissions([ePermissions.EditEscrowWireDisbursementsDisbursed]);
  if (!hasPermission || !isVoidable) {
    return null;
  }

  return (
    <VoidConfirmationModal
      onVoid={onVoid}
      button-data-test-id={`void-wire-button-${wireDisbursement.id}`}
      whatAreWeVoiding="Wire"
      initialValues={{
        checkInHand: false,
        confirmation: false,
        voidReason: '',
        voidReasonDescription: '',
      }}
      validationSchema={voidWireSchema}
      details={<VoidWireDetails wireDisbursement={wireDisbursement} />}
      form={
        <Flex display={'flex'} flexDirection={'column'} gap={1}>
          <VoidReason reasonList={voidReasonOptions} />
          <Confirmation />
          <Alert
            appearance="warning"
            body={`Only Void a Wire if you've confirmed that the payment was unsuccessful in Wells Fargo Payment Manager. Voiding a Wire WILL NOT reverse a completed payment.`}
          ></Alert>
        </Flex>
      }
    />
  );
};
